import axios from "axios";

import { PROMPT_MODEL } from "../utils/Constants";
import {
  MetaTagsFormData,
  MetaTagsResult,
} from "../features/meta-tags-generator/components/types";

const baseUrl = process.env.REACT_APP_BASE_API ?? "";

export const getAiMetaTagsData = async (data: MetaTagsFormData) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `

        Here is input data:

    ${JSON.stringify(data)}
    
Generate ${
      data.header.results
    } SEO-optimized meta tags (titles and descriptions) for the provided URL. Additionally, evaluate and color-code the title and description lengths based on the following color score criteria:

Color Score Criteria:
Title Length:

Website Name: Incorporate the website name, and ensure it's present in all meta tags where appropriate.
Creativity Level: The creativity level is ${
      data.header.creativity
    }, meaning the content should be engaging, dynamic, and enticing, but still relevant to the target audience (job seekers looking for cover letter help).
Keywords: Use the provided keywords for item, naturally in both the title and description.
SEO Guidelines:
Title: Must be under 60 characters. Ensure it's compelling, informative, and optimized for search engines.
Description: Must be concise (under 160 characters), summarize the content effectively, and include the relevant keywords. Aim for a clear call to action or engaging language.
Output Structure: The output should be in the following JSON format, where each entry includes the URL, a title, and a description.
Red: More than 60 characters.
Green: Between 50 and 60 characters (inclusive).
Yellow: Less than 40 characters.
Description Length:

Red: More than 160 characters.
Green: Between 120 and 160 characters (inclusive).
Yellow: Less than 120 characters.
Key Focus Areas:
Website Name: Include the website name in all meta tags (titles and descriptions) where appropriate.
Creativity Level: Maintain a creativity level of ${
      data.header.creativity
    }. The content should be engaging and dynamic, tailored to the target audience (e.g., job seekers seeking cover letter assistance).
Keywords: Seamlessly integrate the provided keywords into both the title and description.
SEO Best Practices:
Title:
Length: Aim for the green range (50-60 characters).
Content: Creative, informative, and enticing to improve click-through rates.
Description:
Length: Aim for the green range (120-160 characters).
Content: Concise and engaging, summarizing the page content effectively. Include relevant keywords naturally, with a clear call to action.
Output Requirements:
Return the results in the following JSON format:

[
  {
    "keywords": "<provided keywords>",
    "websiteUrl": "<provided URL>",
    "result": [
      {
        "title": "<SEO-optimized meta title>",
        "titleColorScore": "<Red/Green/Yellow>",
        "url": "<provided URL>",
        "description": "<SEO-optimized meta description>",
        "descriptionColorScore": "<Red/Green/Yellow>"
      },
      ...
    ]
  }
]
Example Output:
json
Copy
Edit
[
  {
    "keywords": "cover letter, resume, job, AI ATS",
    "websiteUrl": "https://www.jobalope.com/cover-letter/",
    "result": [
      {
        "title": "AI-Powered Cover Letters to Land Your Dream Job – Jobalope",
        "titleColorScore": "Green",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Create the perfect cover letter with AI tools at Jobalope. Stand out to hiring managers and optimize for ATS with our cover letter builder.",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Unlock Job Success with Jobalope’s AI-Optimized Cover Letters",
        "titleColorScore": "Green",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Boost your job applications with AI-enhanced cover letters and ATS-optimized resumes. Get noticed by employers at Jobalope!",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Perfect Your Cover Letter with AI – Jobalope’s Guide",
        "titleColorScore": "Yellow",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Craft cover letters that pass ATS filters and impress recruiters. Use Jobalope’s AI tools to fine-tune your resume and cover letter.",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Create a Winning Cover Letter for Any Job – Jobalope AI Tools",
        "titleColorScore": "Red",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Find out how to create job-winning cover letters with AI-powered tools at Jobalope. Optimize your resume and cover letter for ATS success.",
        "descriptionColorScore": "Green"
      }
    ]
  }
]


Key Features:
Color Scores: Each title and description will be evaluated and labeled with a color score (Red, Green, Yellow) based on the length criteria.
URL: The specific page for which the meta tags are generated.
Title & Description: Creative, SEO-friendly, and tailored to meet the color score guidelines while incorporating keywords effectively.


`;

    const systemPrompt = ` generate`;

    // Preparing the request data
    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];
    // Making the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
        max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content
        .replace(/```json | ```/g, "")
        .trim(); // AI's response in the first choice
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in getTypoPrompt:", error);
    return "An error occurred while processing your request.";
  }
};
export const getMetaTagsRewrite = async (data: MetaTagsResult) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `

        Here is input data:

                 ${JSON.stringify(data)}

    Rewrite the result ${JSON.stringify(
      data.result
    )} for keywords ${JSON.stringify(data.keywords)} for the website url ${
      data.websiteUrl
    }.  

    consider below requirements:

Generate SEO-optimized meta tags (titles and descriptions) for the provided URL. Additionally, evaluate and color-code the title and description lengths based on the following color score criteria:

Color Score Criteria:
Title Length:

Red: More than 60 characters.
Green: Between 50 and 60 characters (inclusive).
Yellow: Less than 40 characters.
Description Length:

Red: More than 160 characters.
Green: Between 120 and 160 characters (inclusive).
Yellow: Less than 120 characters.
Key Focus Areas:
Website Name: Include the website name in all meta tags (titles and descriptions) where appropriate.
Creativity Level: Maintain a creativity level of . The content should be engaging and dynamic, tailored to the target audience (e.g., job seekers seeking cover letter assistance).
Keywords: Seamlessly integrate the provided keywords into both the title and description.
SEO Best Practices:
Title:
Length: Aim for the green range (50-60 characters).
Content: Creative, informative, and enticing to improve click-through rates.
Description:
Length: Aim for the green range (120-160 characters).
Content: Concise and engaging, summarizing the page content effectively. Include relevant keywords naturally, with a clear call to action.
Output Requirements:
Return the results in the following JSON format:

[
  {
    "keywords": "<provided keywords>",
    "websiteUrl": "<provided URL>",
    "result": [
      {
        "title": "<SEO-optimized meta title>",
        "titleColorScore": "<Red/Green/Yellow>",
        "url": "<provided URL>",
        "description": "<SEO-optimized meta description>",
        "descriptionColorScore": "<Red/Green/Yellow>"
      },
      ...
    ]
  }
]
Example Output:
json
Copy
Edit
[
  {
    "keywords": "cover letter, resume, job, AI ATS",
    "websiteUrl": "https://www.jobalope.com/cover-letter/",
    "result": [
      {
        "title": "AI-Powered Cover Letters to Land Your Dream Job – Jobalope",
        "titleColorScore": "Green",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Create the perfect cover letter with AI tools at Jobalope. Stand out to hiring managers and optimize for ATS with our cover letter builder.",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Unlock Job Success with Jobalope’s AI-Optimized Cover Letters",
        "titleColorScore": "Green",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Boost your job applications with AI-enhanced cover letters and ATS-optimized resumes. Get noticed by employers at Jobalope!",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Perfect Your Cover Letter with AI – Jobalope’s Guide",
        "titleColorScore": "Yellow",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Craft cover letters that pass ATS filters and impress recruiters. Use Jobalope’s AI tools to fine-tune your resume and cover letter.",
        "descriptionColorScore": "Green"
      },
      {
        "title": "Create a Winning Cover Letter for Any Job – Jobalope AI Tools",
        "titleColorScore": "Red",
        "url": "https://www.jobalope.com/cover-letter/",
        "description": "Find out how to create job-winning cover letters with AI-powered tools at Jobalope. Optimize your resume and cover letter for ATS success.",
        "descriptionColorScore": "Green"
      }
    ]
  }
]


Result should be JSON format.
            `;
    const systemPrompt = ` generate`;

    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    // Making the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
        max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content
        .replace(/```json | ```/g, "")
        .trim(); // AI's response in the first choice
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in getTypoPrompt:", error);
    return "An error occurred while processing your request.";
  }
};

export const getHeaderTagsResponse = async (data: any) => {
  try {
    const userPrompts = `
      Generate structured, SEO-optimized headings for a blog article using the following guidelines:

1. **H1 (Main Heading)**:
   - Serve as the title of the blog.
   - Clearly convey the primary topic.
   - Include the main keyword for SEO.

2. **H2 (Subheadings)**:
   - Divide the content into major sections.
   - Reflect key points or themes of the article.
   - Include relevant secondary keywords.

3. **H3 (Supporting Subsections)**:
   - Provide detailed breakdowns under each H2 heading.
   - Include long-tail keywords or specific elements of the topic.

4. **H4-H6 (Optional)**:
   - Add additional levels of detail or minor sections as needed.
   - Focus on nested information under H3 or finer aspects of the topic.

**Additional Requirements**:
- Ensure the hierarchy is logical and follows HTML structure best practices.
- Keep headings concise (40-60 characters) and engaging.
- Avoid keyword stuffing but ensure natural keyword integration.
- Include headings that improve readability and align with SEO best practices.

**Output Format**:
Provide the headings in an array of objects with the following structure:

       {
    "tag": "h1",
    "headingName": "Main Heading Here",
    "children": [
        {
            "tag": "h2",
            "headingName": "Subheading Example 1",
            "children": [
                {
                    "tag": "h3",
                    "headingName": "Supporting Subsection Example",
                    "children": [
                        {
                            "tag": "h4",
                            "headingName": "Detailed Section Example",
                            "children": []
                        }
                    ]
                }
            ]
        }
    ]
}

            `;

    const systemPrompt = `
                Here is the input to analyze and generate header tags as per the above instructions: ${JSON.stringify(
                  data
                )}
        `;

    // Preparing the request data
    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompts },
    ];

    // Making the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
        max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response
    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content
        .replace(/```json | ```/g, "")
        .trim(); // AI's response in the first choice
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in getTypoPrompt:", error);
    return "An error occurred while processing your request.";
  }
};

export const getHooksGeneratorResponse = async (input: any) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY; // API Key from your environment
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("Missing OpenAI API Key");
  // }

  try {
    // Extract text from the document

    // User prompt instructing the AI on what to do with the document

    const userPrompt = `

    You are a content strategist skilled at writing powerful hooks that instantly grab attention. Given a piece of content, your task is to extract the most compelling ideas and rewrite them as short, punchy hooks that will resonate with a specific audience.

   Generate 5–10 hooks.

Each hook should be 1–2 sentences max.

Make them emotionally charged, curiosity-driven, or benefit-focused.

Tailor the tone and language to match the specified audience.

Avoid clickbait; keep it honest but irresistible.
 
`;

    // const systemPrompt = "Here is content - "+ input.
    const systemPrompt = `Here is the content: ${input.topic}. The target audience is: ${input.targetAudience}.`;

    // Preparing the request data
    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];
    // Making the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
        max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content
        .replace(/```json | ```/g, "")
        .trim(); // AI's response in the first choice
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in getTypoPrompt:", error);
    return "An error occurred while processing your request.";
  }
};

// export const rewriteOptimizeContent = async (
//   content: string,
//   language: string
// ) => {
//   const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
//   const apiUrl = "https://api.openai.com/v1/chat/completions";

//   try {
//     const userPrompt = `
// Here is the input content:

// ${content}

// Please rewrite and optimize this content for SEO purposes. The rewritten content should be engaging, informative, and optimized for both search engines and readability. Ensure the following:

// - **SEO Focus**: Use relevant keywords naturally in the text to improve search engine rankings.
// - **Engagement**: Make the content more engaging and compelling, ensuring it attracts readers' attention.
// - **Clarity**: Keep the content clear, easy to read, and to the point.
// - **Length**: Ensure the content is well-balanced in terms of length – concise but detailed enough to provide value.
// - **Formatting**: Format the content using HTML tags (such as <p> for paragraphs, <h2> for subheadings, etc.) to make it ready for publication on a web page.

// The output should be a JSON object with a single key "rewrittenText" containing the rewritten and HTML-formatted content.

// Example output:
// {
//     "rewrittenText": "<h2>The Wonders of Coffee</h2><p>Coffee, a beloved global beverage, is crafted from roasted coffee beans.</p><p>Its rich flavor and caffeine boost make it a daily essential for many.</p>"
// }

// Generate the response in the language: ${language}
//     `;

//     const systemPrompt = `Generate optimized and rewritten content as per the instructions provided.`;

//     const requestData = [
//       { role: "system", content: systemPrompt },
//       { role: "user", content: userPrompt },
//     ];

//     const response = await axios.post(
//       apiUrl,
//       {
//         model: PROMPT_MODEL.GPT4o,
//         messages: requestData,
//         temperature: PROMPT_MODEL.TEMP,
//         max_tokens: PROMPT_MODEL.MAX_TOKENS,
//         response_format: { "type": "json_object" }
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${apiKey}`,
//         },
//       }
//     );

//     if (response.status === 200) {
//       return response.data.choices[0].message.content;
//     } else {
//       throw new Error(`Failed with status code: ${response.status}`);
//     }
//   } catch (error) {
//     console.error("Error in rewriteOptimizeContent:", error);
//     return "An error occurred while processing your request.";
//   }
// };

export const rewriteOptimizeContent = async (
  content: string,
  language: string
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
Here is the input content:

${content}

Please rewrite and optimize this content for SEO purposes while preserving the structural intent of the input. The rewritten content should be engaging, informative, and optimized for both search engines and readability. Ensure the following:

- **SEO Focus**: Incorporate relevant keywords naturally to enhance search engine rankings without keyword stuffing.
- **Engagement**: Craft compelling and captivating content to attract and retain readers' attention.
- **Clarity**: Ensure the content is clear, concise, and easy to understand.
- **Length**: Balance brevity and detail to provide value without unnecessary fluff.
- **Formatting**:
  - Detect and preserve the hierarchy of headings in the input (e.g., Title, H1, H2, H3, etc.).
  - Format the output using HTML tags:
    - Use <h1> for the main title (if a title is indicated, e.g., "Title:").
    - Use <h2> for primary subheadings (equivalent to H1 in input).
    - Use <h3> for secondary subheadings (equivalent to H2 in input).
    - Use <h4> for tertiary subheadings (equivalent to H3 in input), and so on.
    - Use <p> for paragraphs and plain text.
  - If the input explicitly labels sections (e.g., "Title:", "H1:", "H2:"), reflect that structure in the output.
  - If no explicit labels are provided, infer a logical structure based on formatting cues (e.g., newlines, indentation, or emphasis) and apply appropriate headings.
  - Ensure the hierarchy is clear so readers can distinguish between the main title, subheadings, and body text.

The output should be a JSON object with a single key "rewrittenText" containing the rewritten and HTML-formatted content.

Example input:
Title: The Joy of Baking
H1: Why Baking is Fun
Baking brings creativity to the kitchen.
H2: Easy Recipes
Try simple cakes and cookies.

Example output:
{
    "rewrittenText": "<h1>The Joy of Baking</h1><h2>Why Baking is Fun</h2><p>Baking sparks creativity, transforming simple ingredients into delicious treats.</p><h3>Easy Recipes</h3><p>Start with beginner-friendly recipes like fluffy cakes or chewy cookies.</p>"
}

Generate the response in the language: ${language}
    `;

    const systemPrompt = `You are an expert content writer specializing in SEO and user engagement. Rewrite the provided content according to the user's instructions, ensuring proper formatting and structure.`;

    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];
    // Making the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o,
        messages: requestData,
        temperature: PROMPT_MODEL.TEMP,
        max_tokens: PROMPT_MODEL.MAX_TOKENS,
        response_format: { type: "json_object" },
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content;
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in rewriteOptimizeContent:", error);
    return "An error occurred while processing your request.";
  }
};

export const getSeoGradedContent = async (content: string) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    // User prompt for rewriting and optimizing the content
    const userPrompt = `
  
    Analyze the following article content for SEO effectiveness and AI-generated content estimation:

Article Content:
${JSON.stringify(content)}

Your Task:

Evaluate the content for SEO effectiveness based on the following factors:

Keyword Usage: Check keyword presence, density, and relevance throughout the content, title, and meta description.
Meta Description Quality: Assess the presence, length, uniqueness, and use of primary keywords.
Page Title Score: Evaluate the title length, keyword inclusion, relevance, and click-through potential.
Header Optimization: Inspect the use of H1, H2, and other header tags for structure and keyword relevance.
Content Readability: Measure readability using standard metrics (e.g., Flesch Reading Ease) and assess sentence/paragraph clarity.
Internal and External Linking: Check for the presence and relevance of internal and external links.
Overall Content Structure: Evaluate the logical flow, use of bullet points, subheadings, and accessibility features like alt text.
Provide a detailed breakdown of scores for the following key aspects:

Page Title Score
Meta Description Score
Content Score
Offer actionable suggestions for improving the SEO ranking and content quality.

Output Format: Ensure the response is in JSON format, with detailed feedback and scores for each category.

Key Rules for Scoring:

Page Title Score:
Ideal length: 50–60 characters.
Includes primary keyword(s).
Grabs attention and encourages clicks.
Avoids keyword stuffing.
Avoids duplication.
Meta Description Score:
Ideal length: 120–160 characters.
Includes primary and secondary keywords.
Provides a compelling summary of the content.
Avoids duplication with other meta descriptions.
Unique and aligned with the page content.
Content Score:
Minimum content length: 300 words (preferably 500+).
Keywords distributed naturally without overuse.
Includes at least one H1 tag and several H2/H3 tags for subtopics.
Uses clear, concise sentences and paragraphs.
Contains internal and external links that are relevant and functional.
Multimedia elements (images, videos) include alt text and captions when necessary.
Free of grammatical errors and spelling mistakes.

Example Output Format:

{
  "pageTitleScore": [
    {
      "text": "You should add a Page Title.",
      "grade": "Poor"
    },
    {
      "text": "The Page Title is too short. 60 characters available. (0 of 60 characters used).",
      "grade": "Excellent"
    }
  ],
  "pageMetaDescriptionScore": [
    {
      "text": "You should add a Meta description.",
      "grade": "Good"
    },
    {
      "text": "The Meta description is too short. 160 characters available. (0 of 160 characters used).",
      "grade": "Excellent"
    }
  ],
  "contentScore": [
    {
      "text": "You should add a H1 header.",
      "grade": "Poor"
    },
    {
      "text": "Your text doesn't contain enough words. A minimum of 300 words is recommended.",
      "grade": "Excellent"
    }
  ]
}
`;

    // System prompt for guiding the model
    const systemPrompt = `Generate output`;

    // Preparing the request data
    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    // Making the API call to get the rewritten content
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
        max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content
        .replace(/```json | ```/g, "")
        .trim(); // AI's response in the first choice
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error in seoGradedContent:", error);
    return "An error occurred while processing your request.";
  }
};
// export const getSeoContentGrader = async (content: SeoTips) => {
//   const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
//   const apiUrl = "https://api.openai.com/v1/chat/completions";

//   try {
//     // User prompt for rewriting and optimizing the content
//     const userPrompt = `
//  Evaluate the provided article for SEO optimization . Assess the article's performance in three categories: Page Title Score, Meta Description Score, and Content Score. Provide the following:

// An overall SEO grade (percentage).
// Specific actionable tips to improve the SEO, clearly indicating whether each aspect is good or needs improvement.
// Respond in the following JSON format:

// {
//   "grade": <percentage_grade>,
//   "tips": {
//     "page_title_score": [
//       {
//         "isGood": <true_or_false>,
//         "value": "<specific_feedback_on_page_title>"
//       }
//     ],
//     "meta_description_score": [
//       {
//         "isGood": <true_or_false>,
//         "value": "<specific_feedback_on_meta_description>"
//       }
//     ],
//     "content_score": [
//       {
//         "isGood": <true_or_false>,
//         "value": "<specific_feedback_on_content>"
//       }
//     ]
//   },
//   "top_serp": [
//     "<top_10_competing_SERP_results>"
//   ],
//   "recommended_terms": [
//     "<specific_terms_to_include>"
//   ]
// }
// Input Details:
// {
//   "article_content": ${JSON.stringify(content)},
// }
// `;

//     // System prompt for guiding the model
//     const systemPrompt = `Generate output`;

//     // Preparing the request data
//     const requestData = [
//       { role: "system", content: systemPrompt },
//       { role: "user", content: userPrompt },
//     ];

//     // Making the API call to get the rewritten content
//     const response = await axios.post(
//       apiUrl,
//       {
//         model: PROMPT_MODEL.GPT4o, // Use the GPT-4 model
//         messages: requestData, // Pass the user and system prompts
//         temperature: PROMPT_MODEL.TEMP, // Adjust for more creative responses
//         max_tokens: PROMPT_MODEL.MAX_TOKENS, // Limit the number of tokens
//       },
//       {
//         headers: {
//           "Content-Type": "application/json",
//           Authorization: `Bearer ${apiKey}`,
//         },
//       }
//     );

//     // Check if the response is successful
//     if (response.status === 200) {
//       //replace(/```json | ```/g, "").trim()
//       return response.data.choices[0].message.content
//         .replace(/```json\n/, "")
//         .replace(/\n```/, "");
//     } else {
//       throw new Error(`Failed with status code: ${response.status}`);
//     }
//   } catch (error) {
//     console.error("Error in seoGradedContent:", error);
//     return "An error occurred while processing your request.";
//   }
// };

export const getSeoContentGrader = async (content: any) => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
    Analyze the following article content for SEO effectiveness and AI-generated content estimation:

    Article Content:
    ${JSON.stringify(content)}

    Your Task:

    Evaluate the content for SEO effectiveness based on the following factors:

    Keyword Usage: Check keyword presence, density, and relevance throughout the content, title, and meta description.
    Meta Description Quality: Assess the presence, length, uniqueness, and use of primary keywords.
    Page Title Score: Evaluate the title length, keyword inclusion, relevance, and click-through potential.
    Header Optimization: Inspect the use of H1, H2, and other header tags for structure and keyword relevance.
    Content Readability: Measure readability using standard metrics (e.g., Flesch Reading Ease) and assess sentence/paragraph clarity.
    Internal and External Linking: Check for the presence and relevance of internal and external links.
    Overall Content Structure: Evaluate the logical flow, use of bullet points, subheadings, and accessibility features like alt text.

    Provide a detailed breakdown of scores for the following key aspects:

    Page Title Score
    Meta Description Score
    Content Score
    Offer actionable suggestions for improving the SEO ranking and content quality.

    Output Format: Ensure the response is in JSON format, with detailed feedback and scores for each category.

    Key Rules for Scoring:

    Page Title Score:
    Ideal length: 50–60 characters.
    Includes primary keyword(s).
    Grabs attention and encourages clicks.
    Avoids keyword stuffing.
    Avoids duplication.
    Meta Description Score:
    Ideal length: 120–160 characters.
    Includes primary and secondary keywords.
    Provides a compelling summary of the content.
    Avoids duplication with other meta descriptions.
    Unique and aligned with the page content.
    Content Score:
    Minimum content length: 300 words (preferably 500+).
    Keywords distributed naturally without overuse.
    Includes at least one H1 tag and several H2/H3 tags for subtopics.
    Uses clear, concise sentences and paragraphs.
    Contains internal and external links that are relevant and functional.
    Multimedia elements (images, videos) include alt text and captions when necessary.
    Free of grammatical errors and spelling mistakes.

    Example Output Format:

    {
      "pageTitleScore": [
        {
          "text": "You should add a Page Title.",
          "grade": "Poor"
        },
        {
          "text": "The Page Title is too short. 60 characters available. (0 of 60 characters used).",
          "grade": "Excellent"
        }
      ],
      "pageMetaDescriptionScore": [
        {
          "text": "You should add a Meta description.",
          "grade": "Good"
        },
        {
          "text": "The Meta description is too short. 160 characters available. (0 of 160 characters used).",
          "grade": "Excellent"
        }
      ],
      "contentScore": [
        {
          "text": "You should add a H1 header.",
          "grade": "Poor"
        },
        {
          "text": "Your text doesn't contain enough words. A minimum of 300 words is recommended.",
          "grade": "Excellent"
        }
      ]
    }
    `;

    const systemPrompt = `Generate output`;

    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    const response = await axios.post(
      apiUrl,
      {
        model: PROMPT_MODEL.GPT4o,
        messages: requestData,
        temperature: PROMPT_MODEL.TEMP,
        max_tokens: PROMPT_MODEL.MAX_TOKENS,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );

    if (response.status === 200) {
      return response.data.choices[0].message.content
        .replace(/```json\n/, "")
        .replace(/\n```/, "");
    } else {
      throw new Error(`Failed with status code: ${response.status}`);
    }
  } catch (error) {
    console.error("Error in seoGradedContent:", error);
    return "An error occurred while processing your request.";
  }
};

export const FaqsGeneratorResponse = async (
  topic: string,
  keywords: string, // Add keyword as a parameter
  content: string,
  language: string,
  numberOfFaqs: number, // Accept the number of FAQs
  tone: string // Accept the tone
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    // Ensure topic length is adequate for generating FAQs
    if (topic.length < 15) {
      throw new Error("Topic must be at least 15 characters long");
    }

    // User prompt for generating FAQs with the updated guidelines
    const userPrompt = `
      You are an expert SEO strategist and content writer. Your task is to generate high-quality, SEO-optimized FAQs based on the input provided. 
      The FAQs should target relevant search queries with good search volume and align with user intent.

      Input Details:
      Focus Keyword: ${topic}
      Secondary Keywords (Optional): ${keywords ? keywords : "None"}
      Content (Optional): ${
        content ? content : "No additional content provided"
      }

      Instructions:
      1. Analyze User Intent: Use the provided focus keyword and secondary keywords to identify common user queries.
      2. Generate FAQ Questions: Create ${numberOfFaqs} FAQs that:
         - Are directly relevant to the topic or content.
         - Naturally include the focus keyword or secondary keywords.
         - Reflect questions users are likely to ask, such as those seen in “People Also Ask” or auto-suggestions on Google.
      3. Write Clear Answers: Provide a concise (50–100 words) answer for each FAQ. Ensure answers:
         - Address the question directly and comprehensively.
         - Use the focus keyword naturally.
         - Are structured for Google’s featured snippets, like paragraphs, bullet points, or lists.
      4. Suggest Related FAQs (Optional): If applicable, include additional related questions to enhance content depth.
      5. The output should be in ${language}.
      6. The tone should be: ${tone}.

      output example format:
       [
         {
          "question": "What is your name1?",
          "answer": "My Name is chatgpt1."
        },
        {
           "question": "What is your name2?",
           "answer": "My Name is chatgpt2."
         }
       ]
    `;

    // System prompt for guiding the model
    const systemPrompt = `Generate SEO-optimized FAQs based on the provided input details, focusing on user intent and search optimization.`;

    // Preparing the request data
    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    // Making the API call to get the FAQs
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4", // Use the GPT-4 model
        messages: requestData, // Pass the user and system prompts
        temperature: 0.7, // Moderate creativity for FAQ generation
        max_tokens: 1500, // Limit the number of tokens
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      const faqs = response.data.data.choices[0].message.content
        .replace(/```json\n/, "")
        .replace(/\n```/, "");
      return faqs.trim(); // Clean up any excess whitespace
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
    // const response = await axios.post(
    //   apiUrl,
    //   {
    //     model: "gpt-4", // Use the GPT-4 model
    //     messages: requestData, // Pass the user and system prompts
    //     temperature: 0.7, // Moderate creativity for FAQ generation
    //     max_tokens: 1500, // Limit the number of tokens
    //   },
    //   {
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${apiKey}`,
    //     },
    //   }
    // );

    // // Check if the response is successful and handle it
    // if (response.status === 200) {
    //   // Get the FAQ content from the first choice
    //   const faqs = response.data.choices[0].message.content
    //     .replace(/```json\n/, "")
    //     .replace(/\n```/, "");
    //   return faqs.trim(); // Clean up any excess whitespace
    // } else {
    //   throw new Error(`Failed with status code: ${response.status}`);
    // }
  } catch (error) {
    console.error("Error in FaqsGeneratorResponse:", error);
    return "An error occurred while processing your request.";
  }
};

export const expandTextAiResponse = async (
  text: string,
  language: string = "English"
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
      Expand the following text to make it more detailed and comprehensive while keeping the original meaning. The expanded text should be in ${language} and approximately double the length. Ensure the content flows naturally and adds value.

      Original Text: ${text}
    `;

    const systemPrompt = `You are a helpful assistant that expands text to provide more detail and depth. Only return the expanded text without additional formatting or labels.`;

    const requestData = [
      { role: "system", content: systemPrompt },
      { role: "user", content: userPrompt },
    ];

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: requestData,
        temperature: 0.7,
        max_tokens: 1500,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error expanding text:", error);
    throw error;
  }
};

export const shortenTextAiResponse = async (
  text: string,
  language: string = "English"
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
      Condense the following text to make it more concise while preserving key information. The shortened text should be in ${language} and approximately half the original length. Remove unnecessary details but maintain the core meaning.

      Original Text: ${text}
    `;

    const systemPrompt = `You are an editor that shortens text effectively. Only return the condensed text without additional formatting.`;

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error shortening text:", error);
    throw error;
  }
};

export const rephraseTextAiResponse = async (
  text: string,
  language: string = "English"
) => {
  try {
    const userPrompt = `
      Rephrase the following text to use different wording while maintaining the original meaning. Use ${language} and keep the length similar. Make the language more professional and polished.

      Original Text: ${text}
    `;

    const systemPrompt = `You are a professional editor that rephrases text effectively. Return only the rephrased text.`;

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error rephrasing text:", error);
    throw error;
  }
};

export const simplifyTextAiResponse = async (
  text: string,
  language: string = "English"
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
      Simplify the following text to make it more understandable for a general audience. Use ${language} and basic vocabulary while preserving the core meaning. Break down complex ideas into simpler terms.

      Original Text: ${text}
    `;

    const systemPrompt = `You simplify complex text effectively. Return only the simplified version.`;

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error simplifying text:", error);
    throw error;
  }
};

export const bulletizeTextAiResponse = async (
  text: string,
  language: string = "English"
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    const userPrompt = `
      Convert the following text into clear bullet points in ${language}. Organize key ideas logically and use concise phrasing. Include all important information in list format.

      Original Text: ${text}
    `;

    const systemPrompt = `You convert text to organized bullet points. Return only the list without introductory text.`;
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error creating bullet points:", error);
    throw error;
  }
};

export const extractKeywordsAiResponse = async (
  text: string,
  filter: "single" | "short" | "long"
) => {
  const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    // Determine the keyword length based on the filter
    let keywordLength = "";
    if (filter === "short") {
      keywordLength = "exactly 3-word phrases";
    } else if (filter === "long") {
      keywordLength = "exactly 4-word phrases";
    } else {
      keywordLength = "single words";
    }

    // Construct the prompt with stricter instructions
    const userPrompt = `
          Extract the most relevant keywords from the following text. The keywords must be ${keywordLength} that best represent the main topics or themes of the content. Only return keywords that exactly match the specified word count (e.g., no more, no less). Return the list of keywords, one per line, without any additional text or numbering.

          Text: ${text}
      `;

    const systemPrompt =
      "You are an AI that extracts relevant keywords from text based on strict word count rules.";

    // Make the API call
    const response = await axios.post(
      apiUrl,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${apiKey}`,
        },
      }
    );

    // Parse the response into an array of keywords
    const keywordsText = response.data.choices[0].message.content.trim();
    const keywords = keywordsText.split("\n").map((k: string) => k.trim());
    return keywords;
  } catch (error) {
    console.error("Error extracting keywords:", error);
    throw error;
  }
};

interface GenerateCaptionAndHashtagsResponse {
  caption: string;
  hashtags: string[];
  captionList: string[];
}

export const generateCaptionAndHashtagsAiResponse = async (
  topic: string,
  language: string,
  platform: string,
  numberOfHashtags: number,
  captionValue: number,
  writingTone: string,
  image?: File
): Promise<GenerateCaptionAndHashtagsResponse> => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("OpenAI API key is not defined in environment variables");
  // }

  try {
    //     let userPrompt = `
    // Generate a caption and a list of hashtags for the following topic. The caption should be in ${language}, with a ${writingTone} tone, suitable for ${platform}. Then, provide exactly ${numberOfHashtags} relevant hashtags for the topic, also in ${language}. Each hashtag should start with "#" and contain no spaces (e.g., #SocialMedia). Separate the caption and the hashtags with the line "-----".

    // Topic: ${topic}
    // `;
    let userPrompt = `
Generate ${captionValue} unique captions and a list of ${numberOfHashtags} relevant hashtags for the following topic. Each caption should be in ${language}, with a ${writingTone} tone, suitable for ${platform}. After generating all captions, provide a single list of ${numberOfHashtags} hashtags in ${language}. Each hashtag must start with "#" and contain no spaces (e.g., #SocialMedia). Separate the captions and the hashtags with the line "-----".

Topic: ${topic}
`;

    // If an image is provided, modify the prompt to include its description
    if (image) {
      const imageDescription = await analyzeImage(image); // Use image analysis
      //       userPrompt = `
      // Generate a caption and a list of hashtags based on the following image description and topic. The caption should be in ${language}, with a ${writingTone} tone, suitable for ${platform}. Then, provide exactly ${numberOfHashtags} relevant hashtags, also in ${language}. Each hashtag should start with "#" and contain no spaces (e.g., #SocialMedia). Separate the caption and the hashtags with the line "-----".

      // Image Description: ${imageDescription}
      // Topic: ${topic}
      // `;
      userPrompt = `
Generate ${captionValue} unique captions and a list of ${numberOfHashtags} relevant hashtags based on the following image description and topic. Each caption should be in ${language}, with a ${writingTone} tone, suitable for ${platform}. After generating all captions, provide a single list of ${numberOfHashtags} hashtags in ${language}. Each hashtag must start with "#" and contain no spaces (e.g., #SocialMedia). Separate the captions and the hashtags with the line "-----".

Image Description: ${imageDescription}
Topic: ${topic}
`;
    }

    // Define the system prompt
    const systemPrompt =
      "You are an AI that generates social media captions and hashtags based on given topics and specifications, optionally incorporating image descriptions.";

    // Make the API call
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.5,
        max_tokens: 1000,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      const responseText = response.data.data.choices[0].message.content.trim();

      const [captionPart, hashtagsPart] = responseText.split("-----");
      const caption = captionPart.trim();
      const captionList = caption
        .split(/\n+/) // Split by new lines
        .map((line: string) => {
          // Remove 'Caption X:' or 'X.' from beginning, optionally quoted
          const match = line.match(
            /^(?:Caption\s*\d*:|Caption:|\d+\.)\s*"?(.+?)"?$/
          );
          return match ? match[1].trim() : null;
        })
        .filter(Boolean) as string[];
      const hashtags = hashtagsPart
        .split("\n")
        .map((h: string) => h.trim())
        .filter((h: string) => h.startsWith("#") && h.length > 1); // Ensure valid hashtags

      return { caption, hashtags, captionList };
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }

    // Parse the response
  } catch (error) {
    console.error("Error generating caption and hashtags:", error);
    throw error instanceof Error
      ? error
      : new Error(
          "An unknown error occurred while generating caption and hashtags"
        );
  }
};

// Helper function to analyze the image (mock implementation for now)
async function analyzeImage(image: File): Promise<string> {
  // Mock implementation: Returns a static description for testing
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve("A professional resume and cover letter services webpage"); // Mock description for Coozmoo-like image
    }, 1000); // Simulate API delay
  });
}

// Helper function to convert image to base64 (optional, not used here but included for reference)
async function convertImageToBase64(image: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
}

export interface GenerateBlogPostResponse {
  title: string;
  content: string;
  wordCount: number;
}

export const generateBlogPostAiResponse = async (
  topic: string,
  targetedKeyword: string | undefined,
  language: string,
  creativity: string,
  wordCountTarget: number = 2000
): Promise<GenerateBlogPostResponse> => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("OpenAI API key is not defined in environment variables");
  // }

  try {
    const temperature = creativity === "high" ? 0.9 : 0.5;

    let userPrompt = `
Generate a blog post in ${language} with ${creativity} creativity on the following topic: "${topic}". 
The post should be approximately ${wordCountTarget} words long.

Structure the content as:
1. An introduction that hooks the reader  
2. Several numbered sections with engaging subheadings, wrapped in <h4> tags  
3. A final conclusion prefixed by <h3>Conclusion</h3>

Format using HTML:
- Use <p> for paragraphs  
- Use <h4> for subheadings  
- Separate the title and content with "-----"  

Make the post informative, engaging, and well-organized.
`;

    const systemPrompt =
      "You are an AI blog writer that generates high-quality, engaging blog posts based on given topics, creativity levels, and optional keywords.";

    const estimatedTokens = Math.ceil(wordCountTarget * 1.5);
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature,
        max_tokens: Math.min(estimatedTokens, 4000),
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      const responseText = response.data.data.choices[0].message.content.trim();
      const parts = responseText.split("-----");
      if (parts.length < 2) {
        throw new Error(
          "Invalid response format: Expected at least title and content separated by '-----'"
        );
      }

      const title = parts[0].trim();
      const remainingText = parts.slice(1).join("-----").trim();
      const [content, wordCountLine] = remainingText.split("\n").reduce(
        (acc: [string, string], line: string) => {
          if (line.startsWith("Word Count: ")) {
            acc[1] = line;
          } else {
            acc[0] += (acc[0] ? "\n" : "") + line;
          }
          return acc;
        },
        ["", ""] as [string, string]
      );

      const wordCountMatch = wordCountLine.match(/Word Count: (\d+)/);
      const wordCount = wordCountMatch
        ? parseInt(wordCountMatch[1], 10)
        : content.split(/\s+/).length;

      return { title, content: content.trim(), wordCount };
      // return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error generating blog post:", error);
    throw error instanceof Error
      ? error
      : new Error("An unknown error occurred while generating the blog post");
  }
};

export const generateBlogTitlesAiResponse = async (
  topic: string,
  language: string,
  creativity: string
): Promise<string[]> => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("OpenAI API key is not defined in environment variables");
  // }

  try {
    const temperature = creativity === "high" ? 0.9 : 0.5;
    const prompt = `
Generate 5 creative and engaging blog titles for the following topic in ${language} with a ${creativity} creativity level. 
List them as a numbered list without additional commentary.
Topic: ${topic}
    `;
    const systemPrompt = "You are an AI that generates creative blog titles.";

    // const response = await axios.post(
    //   apiUrl,
    //   {
    //     model: "gpt-4",
    //     messages: [
    //       { role: "system", content: systemPrompt },
    //       { role: "user", content: prompt },
    //     ],
    //     temperature,
    //     max_tokens: 250,
    //   },
    //   {
    //     headers: {
    //       "Content-Type": "application/json",
    //       Authorization: `Bearer ${apiKey}`,
    //     },
    //   }
    // );
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: prompt },
        ],
        temperature,
        max_tokens: 250,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === 200 && response.data.success && response.data) {
      const responseText = response.data.data.choices[0].message.content.trim();

      const titles = responseText
        .split("\n")
        .map((line: string) => line.replace(/^\d+\.\s*/, "").trim())
        .filter((line: string) => line.length > 0);
      return titles;
      // return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error generating blog titles:", error);
    throw error instanceof Error
      ? error
      : new Error("An unknown error occurred while generating blog titles");
  }
};
export const socialHeadlineAiResponse = async (
  topic: string,
  platform: string,
  audience?: string,
  tone?: string
) => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  try {
    let userPrompt = `
      Generate 5 engaging headlines optimized for ${platform}.
      Also, provide:
      1. A short analysis of why these headlines are effective.
      2. 5 related topics that complement "${topic}".
    `;

    if (audience || tone) {
      userPrompt += `
        The target audience is: "${audience || "General"}".
        The preferred tone is: "${tone || "Neutral"}".
      `;
    }

    userPrompt += `
      Format the response as:
      - Headlines:
        Headline 1
        Headline 2
        Headline 3
        Headline 4
        Headline 5
      - Analysis:
        [Short Explanation]
      - Related Topics:
        Topic 1 (2 to 3 words)
        Topic 2 (2 to 3 words)
        Topic 3 (2 to 3 words)
        Topic 4 (2 to 3 words)
        Topic 5 (2 to 3 words)
      Do not use dashes or quotation marks in the headlines.
    `;

    const systemPrompt = `You are a content strategist who generates engaging social media headlines and provides an analysis along with related topic suggestions.`;

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.7,
        max_tokens: 250,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      const content = response.data.data.choices[0].message.content.trim();
      console.log("Raw AI Response:", content); // Debug: See raw output

      // Extract data using regex
      const headlinesMatch = content.match(
        /- Headlines:\s*([\s\S]*?)- Analysis:/
      );
      const analysisMatch = content.match(
        /- Analysis:\s*([\s\S]*?)- Related Topics:/
      );
      const relatedTopicsMatch = content.match(/- Related Topics:\s*([\s\S]*)/);

      // Extract and clean headlines
      const headlines =
        headlinesMatch && headlinesMatch[1]
          ? headlinesMatch[1]
              .split("\n")
              .map((h: string) => {
                const cleaned = h
                  .replace(/^-*\s*/g, "") // Remove leading dashes and whitespace
                  .replace(/["“”]/g, "") // Remove all quotation marks
                  .replace(/-/g, "") // Remove any remaining dashes anywhere in the string
                  .trim();
                console.log("Cleaned Headline:", cleaned); // Debug: See each cleaned headline
                return cleaned;
              })
              .filter(Boolean)
          : ["No headlines available."];

      // Extract analysis
      const analysis =
        analysisMatch && analysisMatch[1]
          ? analysisMatch[1].trim()
          : "No analysis available.";

      // Extract related topics
      const relatedTopics =
        relatedTopicsMatch && relatedTopicsMatch[1]
          ? relatedTopicsMatch[1]
              .split("\n")
              .map((t: string) => {
                const cleanedTopic = t.replace(/^-*\s*/g, "").trim();
                const sanitizedTopic = cleanedTopic.replace(/["“”]/g, "");
                const words = sanitizedTopic.split(" ");
                return words.length > 3
                  ? words.slice(0, 3).join(" ")
                  : sanitizedTopic;
              })
              .filter(Boolean)
          : ["No related topics available."];

      console.log("Final Headlines Array:", headlines); // Debug: See final array

      return { headlines, analysis, relatedTopics };
    } else {
      throw new Error(`Failed with status code: ${response.status} `);
    }
  } catch (error) {
    console.error("Error generating AI response:", error);
    return {
      headlines: ["Failed to generate headlines. Try again later."],
      analysis: "Failed to generate analysis.",
      relatedTopics: ["No related topics available."],
    };
  }
};

export const getKeywordsFromAI = async (
  text: string,
  filter: "short" | "long"
): Promise<string[]> => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("OpenAI API key is not defined in environment variables.");
  // }

  const wordCount = filter === "short" ? 2 : 3;
  const systemPrompt = `You are an expert SEO assistant specializing in keyword extraction. Your task is to identify the most relevant ${wordCount}-word phrases from the provided text, focusing on frequency and contextual significance. Exclude generic or irrelevant phrases. Return only a list of the top 10 phrases, one per line, without additional text or formatting.`;

  const userPrompt = `
    Text: ${text}

    Identify the top 10 most relevant ${wordCount}-word phrases based on frequency and contextual importance. List each phrase on a new line.
  `;

  try {
    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.7,
        max_tokens: 200,
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );

    // Return the AI's response

    if (response.status === 200 && response.data.success && response.data) {
      const keywords = response.data.data.choices[0].message.content
        .trim()
        .split("\n");
      return keywords
        .map((kw: string) => kw.trim())
        .filter((kw: string) => kw && kw.split(" ").length === wordCount);
      // return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(
        `OpenAI API request failed with status: ${response.status}`
      );
    }
  } catch (error) {
    console.error("Error fetching keywords from AI:", error);
    return [];
  }
};

export interface GenerateOutlineResponse {
  title: string;
  sections: { heading: string; subpoints: string[] }[];
}

export const generateOutlineAiResponse = async (
  topic: string,
  blogTopic: string,
  language: string,
  tone: string,
  structure: number
): Promise<GenerateOutlineResponse> => {
  // const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
  // const apiUrl = "https://api.openai.com/v1/chat/completions";

  // if (!apiKey) {
  //   throw new Error("OpenAI API key is not defined in environment variables");
  // }

  try {
    const userPrompt = `
Generate a detailed outline for a blog post in ${language} with a ${tone} tone on the topic: "${blogTopic}". 
The outline must incorporate these important points: "${topic}".
The structure must have exactly ${structure} main sections, each with exactly 3 subpoints.

Format the output as:
- A title for the blog post
- A separator line "-----"
- ${structure} main sections, each formatted as:
  - A Roman numeral (e.g., I., II., III., etc.) followed by the section heading
  - Three subpoints labeled A., B., and C., each on a new line

Example format for ${structure} sections:
Title: Example Blog Post
-----
I. Section Heading 1
   A. Subpoint 1
   B. Subpoint 2
   C. Subpoint 3
II. Section Heading 2
   A. Subpoint 1
   B. Subpoint 2
   C. Subpoint 3
[... up to ${structure} sections]

Ensure the outline is clear, organized, detailed, and strictly adheres to the ${structure}-section structure with 3 subpoints per section. Do not generate more or fewer than ${structure} sections.
`;

    const systemPrompt =
      "You are an AI outline generator that creates detailed, well-structured, and engaging blog post outlines with a specified number of sections, each with exactly 3 subpoints, based on given topics, tones, and section counts.";

    const estimatedTokens = Math.ceil(structure * 150 + 300);

    const response: any = await axios.post(
      `${baseUrl}/api/proxy-ai`,
      {
        model: "gpt-4",
        messages: [
          { role: "system", content: systemPrompt },
          { role: "user", content: userPrompt },
        ],
        temperature: 0.8,
        max_tokens: Math.min(estimatedTokens, 2000),
      },
      {
        headers: {
          // Authorization: `Bearer ${"process.env.REACT_OPEN_API_TOKEN"}`, // or your token variable
          Authorization: `Bearer ${process.env.REACT_APP_OPEN_API_TOKEN}`, // or your token variable
          "Content-Type": "application/json",
        },
      }
    );
    if (response.status === 200 && response.data.success && response.data) {
      const responseText = response.data.data.choices[0].message.content.trim();
      const parts = responseText.split("-----");
      if (parts.length < 2) {
        throw new Error(
          "Invalid response format: Expected title and sections separated by '-----'"
        );
      }

      const title = parts[0].trim();
      const sectionsText = parts[1].trim();
      const sectionLines = sectionsText
        .split("\n")
        .map((line: string) => line.trim());

      const sections: { heading: string; subpoints: string[] }[] = [];
      let currentSection: { heading: string; subpoints: string[] } | null =
        null;

      for (const line of sectionLines) {
        if (line.match(/^[IVX]+\.\s.+/)) {
          if (currentSection) {
            sections.push(currentSection);
          }
          currentSection = { heading: line, subpoints: [] };
        } else if (currentSection && line.match(/^[A-C]\.\s.+/)) {
          currentSection.subpoints.push(line);
        }
      }
      if (currentSection) {
        sections.push(currentSection);
      }

      if (
        sections.length !== structure ||
        sections.some((s) => s.subpoints.length !== 3)
      ) {
        throw new Error(
          `Generated outline does not match the requested ${structure}-section, 3-subpoint structure`
        );
      }

      return { title, sections };
      // return response.data.data.choices[0].message.content.trim();
    } else {
      throw new Error(
        `OpenAI API request failed with status: ${response.status}`
      );
    }
  } catch (error) {
    console.error("Error generating outline:", error);
    throw error instanceof Error
      ? error
      : new Error("An unknown error occurred while generating the outline");
  }
};
